import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import { getTypeColor } from '@/utils/nodeTypeMapper';
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';

const NodeInitiativeInclusionList = ({ clickedNode, energyGroup }) => {
  const color = getTypeColor(clickedNode.type) || '#1976d2';

  const inclusions = useMemo(
    () =>
      getInitiativeInclusionsForNode(clickedNode.energyTreeNodeId, energyGroup),
    [clickedNode.energyTreeNodeId, energyGroup],
  );

  if (!clickedNode || !energyGroup) return null;

  if (!inclusions.length) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {/* Header row */}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1 }}>
        <EnergySavingsLeafRoundedIcon
          sx={{
            fontSize: 18,
            opacity: 0.8,
          }}
        />
        <Typography variant="subtitle2" fontWeight={600}>
          Inkluderet i initiativer
        </Typography>
        <Chip
          size="small"
          label={inclusions.length}
          sx={{
            height: 18,
            ml: 0.5,
            '& .MuiChip-label': {
              px: 0.75,
              fontSize: 11,
              lineHeight: '18px',
            },
          }}
        />
      </Stack>

      {/* Initiatives list */}
      <List disablePadding dense>
        {inclusions.map(({ initiative, isDirect, viaNode }, index) => {
          const key =
            initiative.energyInitiativeId ||
            initiative.id ||
            initiative.guid ||
            index;
          const label =
            initiative.name ||
            initiative.title ||
            `Initiativ #${initiative.energyInitiativeId || initiative.id || ''}`.trim();

          return (
            <React.Fragment key={key}>
              <ListItem
                disableGutters
                sx={{
                  py: 0.5,
                  px: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  //   bgcolor: isDirect ? alpha(color, 0.04) : 'transparent',
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {label}
                    </Typography>
                  }
                  secondary={
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mt: 0.25 }}
                    >
                      {isDirect && (
                        <Typography variant="caption" color="text.secondary">
                          Direkte anvendelse
                        </Typography>
                      )}

                      {/* Indirekte via + colored chip for ancestor */}
                      {!isDirect && viaNode && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            Indirekte via
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              viaNode.name ??
                              `Node #${viaNode.energyTreeNodeId || viaNode.EnergyTreeNodeId}`
                            }
                            sx={{
                              borderRadius: 1,
                              borderColor: alpha(
                                getTypeColor(viaNode.type) || color,
                                0.6,
                              ),
                              backgroundColor: getTypeColor(viaNode.type),
                              color: '#fff',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                            variant="filled"
                          />
                        </Stack>
                      )}
                    </Stack>
                  }
                />
              </ListItem>
              {index < inclusions.length - 1 && (
                <Divider variant="middle" sx={{ my: 0.25, opacity: 0.3 }} />
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Stack>
  );
};

NodeInitiativeInclusionList.propTypes = {
  clickedNode: PropTypes.shape({
    energyTreeNodeId: PropTypes.string, // GUID (case-insensitive)
    type: PropTypes.string,
  }),
  energyGroup: PropTypes.shape({
    energyTreeNodes: PropTypes.arrayOf(
      PropTypes.shape({
        energyTreeNodeId: PropTypes.string,
        EnergyTreeNodeId: PropTypes.string,
        parentEnergyTreeNodeId: PropTypes.string,
        ParentEnergyTreeNodeId: PropTypes.string,
      }),
    ),
    energyInitiatives: PropTypes.arrayOf(
      PropTypes.shape({
        energyInitiativeId: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        name: PropTypes.string,
        title: PropTypes.string,
        // Array of GUIDs
        energyTreeNodes: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
  }),
};

export default NodeInitiativeInclusionList;
